import { connect } from 'react-redux';
import { makeStyles, tokens } from '@fluentui/react-components';
import { AppState } from '../../../../redux/reducers';
import { Consultorio, Sector } from '../../../../models/Entities/Turnero';
import * as ConsultoriosStore from '../../../../redux/middleware/Turnero/Turnero.Consultorios.Middleware';
import { ErrorType } from '../../../../models/HttpError';

export interface OwnProps {
  sectorId?: number;
}

interface StateProps {
  consultorios: Consultorio[];
  sectores: Sector[];
  isLoading: boolean;
  failOnLoading: boolean;
  error: ErrorType | undefined;
  isDeleting: boolean;
  isUpdatingEstado: boolean;
}

interface DispatchProps {
  GetConsultorios: typeof ConsultoriosStore.actionCreators.GetConsultorios;
  DeleteConsultorio: typeof ConsultoriosStore.actionCreators.DeleteConsultorio;
  UpdateEstadoConsultorio: typeof ConsultoriosStore.actionCreators.UpdateEstadoConsultorio;
}

export type Props = OwnProps & StateProps & DispatchProps;

export const mapStateToProps = (state: AppState, _own: OwnProps): StateProps => ({
  consultorios: state.Turnero?.Consultorios?.list || [],
  sectores: state.Turnero?.Sectores?.list || [],
  isLoading: state.Turnero?.Consultorios?.isLoadingAll || false,
  failOnLoading: state.Turnero?.Consultorios?.failOnLoadingAll || false,
  error: state.Turnero?.Consultorios?.error,
  isDeleting: state.Turnero?.Consultorios?.isDeletingOne || false,
  isUpdatingEstado: state.Turnero?.Consultorios?.isUpdatingEstado || false,
});

export const mapDispatchToProps = {
  ...ConsultoriosStore.actionCreators,
};

export const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(13em, 1fr))',
    gap: tokens.spacingHorizontalM,
    width: '100%',
  },
  card: {
    position: 'relative',
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalM,
    paddingLeft: `calc(${tokens.spacingHorizontalM} + 4px)`,
    minHeight: '130px',
    minWidth: '13em',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    transitionProperty: 'border-color, box-shadow, transform',
    transitionDuration: '0.15s',
    ':hover': {
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      boxShadow: tokens.shadow4,
    },
    '::before': {
      content: '""',
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0',
      width: '4px',
      borderTopLeftRadius: tokens.borderRadiusMedium,
      borderBottomLeftRadius: tokens.borderRadiusMedium,
      backgroundColor: tokens.colorNeutralStroke2,
    },
  },
  statusLibre: {
    '::before': {
      content: '""',
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0',
      width: '4px',
      borderTopLeftRadius: tokens.borderRadiusMedium,
      borderBottomLeftRadius: tokens.borderRadiusMedium,
      backgroundColor: '#16a34a',
    },
  },
  statusOcupado: {
    '::before': {
      content: '""',
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0',
      width: '4px',
      borderTopLeftRadius: tokens.borderRadiusMedium,
      borderBottomLeftRadius: tokens.borderRadiusMedium,
      backgroundColor: '#dc2626',
    },
  },
  statusReservado: {
    '::before': {
      content: '""',
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0',
      width: '4px',
      borderTopLeftRadius: tokens.borderRadiusMedium,
      borderBottomLeftRadius: tokens.borderRadiusMedium,
      backgroundColor: '#ca8a04',
    },
  },
  statusNoDisponible: {
    backgroundColor: tokens.colorNeutralBackground2,
    '::before': {
      content: '""',
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0',
      width: '4px',
      borderTopLeftRadius: tokens.borderRadiusMedium,
      borderBottomLeftRadius: tokens.borderRadiusMedium,
      backgroundColor: tokens.colorNeutralStroke1,
    },
  },
  cardTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginBottom: tokens.spacingVerticalXXS,
  },
  cardNumber: {
    fontSize: '11px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    fontVariantNumeric: 'tabular-nums',
  },
  cardStatusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    fontSize: '10.5px',
    fontWeight: 600,
    borderRadius: '999px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground2,
  },
  cardStatusBadgeLibre: {
    backgroundColor: '#dcfce7',
    color: '#15803d',
    borderColor: '#bbf7d0' as any,
  },
  cardStatusBadgeOcupado: {
    backgroundColor: '#fee2e2',
    color: '#b91c1c',
    borderColor: '#fecaca' as any,
  },
  cardStatusBadgeReservado: {
    backgroundColor: '#fef3c7',
    color: '#a16207',
    borderColor: '#fde68a' as any,
  },
  cardStatusBadgeNoDisp: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
  },
  cardName: {
    fontWeight: 600,
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.3',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  doctorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginTop: 'auto',
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    minWidth: 0,
  },
  doctorName: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  doctorEmpty: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },
});

export const connector = connect(mapStateToProps, mapDispatchToProps);
